// src/components/Navbar.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./navbar.css";

function Navbar() {

  const userData = sessionStorage.getItem("userData");
  const userDataJS = userData ? JSON.parse(userData) : null;

  const navigate = useNavigate();
  const location = useLocation();

  const role = userDataJS?.role;

  /* =========================================================
     STATES
  ========================================================= */

  const [collapsed, setCollapsed] = useState(
    () => window.innerWidth <= 768
  );

  const [showSidebar, setShowSidebar] = useState(false);

  const [showLogoutPopup, setShowLogoutPopup] = useState(false);

  /* =========================================================
     RESPONSIVE
  ========================================================= */

  useEffect(() => {

    const onResize = () => {

      if (window.innerWidth <= 768) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
        setShowSidebar(false);
      }
    };

    window.addEventListener("resize", onResize);

    return () => window.removeEventListener("resize", onResize);

  }, []);

  /* =========================================================
     CLOSE SIDEBAR ON ROUTE CHANGE
  ========================================================= */

  useEffect(() => {
    setShowSidebar(false);
  }, [location.pathname]);

  /* =========================================================
     HIDE NAVBAR ROUTES
  ========================================================= */

  if (location.pathname === "/newuserlogin") return null;
  if (location.pathname === "/whatsappregister") return null;

  const isLoggedIn = Boolean(userDataJS);

  const HIDE_HAMBURGER_ROUTES = [
    "/",
    "/login",
    "/userlogin",
    "/timesheet",
    "/newuserlogin",
    "/whatsappregister",
  ];

  const onAuthPage = HIDE_HAMBURGER_ROUTES.includes(location.pathname);

  const showHamburger = isLoggedIn && !onAuthPage;

  /* =========================================================
     HELPERS
  ========================================================= */

  const isActive = (path) =>
    location.pathname.startsWith(`/${path}`);

  const go = (path) => {
    navigate(`/${path}`);
    setShowSidebar(false);
  };

  /* =========================================================
     LOGOUT
  ========================================================= */

  function handleLogout() {
    setShowLogoutPopup(true);
  }

  function confirmLogout() {
    sessionStorage.clear();
    setShowLogoutPopup(false);
    navigate("/");
  }

  function closePopup() {
    setShowLogoutPopup(false);
  }

  /* =========================================================
     RENDER
  ========================================================= */

  return (

    <div
      className={`app-shell 
      ${collapsed ? "collapsed" : ""} 
      ${showSidebar ? "show-sidebar" : ""}`}
    >

      {/* =====================================================
          NAVBAR
      ===================================================== */}

      <div className="navbar-container">

        {showHamburger && (

          <button
            className="hamburger"
            aria-label="Open Menu"
            title="Menu"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

        )}

        <img
          src="https://raisonautomation.com/wp-content/uploads/2024/06/Raison-New-Logo-3.png"
          alt="Left Logo"
          className="navbar-logo"
        />

        <div className="company-name">
          {userDataJS ? userDataJS.companyName : ""}
        </div>

        <img
          src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRARO6hC6s2BTUJMntxV9tN6SjhPubkI8Jvug&s"
          alt="Right Logo"
          className="navbar-logo"
        />

      </div>

      {/* =====================================================
          BACKDROP
      ===================================================== */}

      <div
        className="sidebar-backdrop"
        onClick={() => setShowSidebar(false)}
      />

      {/* =====================================================
          SIDEBAR
      ===================================================== */}

      {isLoggedIn && (

        <aside className="sidebar">

          <div className="sidebar-header">

            <h3 className="sidebar-title">
              Menu
            </h3>

            {window.innerWidth > 768 && (

              <button
                className="sidebar-toggle"
                title={collapsed ? "Expand" : "Collapse"}
                onClick={() => setCollapsed((c) => !c)}
              >
                {collapsed ? "☰" : "»"}
              </button>

            )}

          </div>

          {/* =================================================
              ADMIN MENU
          ================================================= */}

          {role === "admindashboard" && (

            <nav className="menu">

              <button
                className={`menu-item ${
                  isActive("admindashboard")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("admindashboard")}
              >
                <span className="icon">🛠️</span>
                <span className="label">
                  Add Device / User
                </span>
              </button>

              <button
                className={`menu-item ${
                  isActive("customerdetails")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("customerdetails")}
              >
                <span className="icon">👥</span>
                <span className="label">
                  Customer Details
                </span>
              </button>

              <button
                className={`menu-item ${
                  isActive("inventory")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("inventory")}
              >
                <span className="icon">📋</span>
                <span className="label">
                  Inventory
                </span>
              </button>

            </nav>

          )}

          {/* =================================================
              USER MENU
          ================================================= */}

          {role !== "admindashboard" && (

            <nav className="menu">

              <button
                className={`menu-item ${
                  isActive("userdashboard")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("userdashboard")}
              >
                <span className="icon">📊</span>
                <span className="label">
                  Current Status
                </span>
              </button>

              <button
                className={`menu-item ${
                  isActive("report")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("report")}
              >
                <span className="icon">📑</span>
                <span className="label">
                  Report
                </span>
              </button>

              <button
                className={`menu-item ${
                  isActive("userprofile")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("userprofile")}
              >
                <span className="icon">👤</span>
                <span className="label">
                  Profile
                </span>
              </button>

            </nav>

          )}

          {/* =================================================
              FOOTER
          ================================================= */}

          <div className="sidebar-footer">

            {role !== "admindashboard" && (

              <button
                className={`menu-item ${
                  isActive("setting")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("setting")}
              >
                <span className="icon">⚙️</span>
                <span className="label">
                  Setting
                </span>
              </button>

            )}

            <button
              className="menu-item"
              onClick={() => {
                handleLogout();
                setShowSidebar(false);
              }}
            >
              <span className="icon">⏻</span>
              <span className="label">
                Logout
              </span>
            </button>

          </div>

        </aside>

      )}

      {/* =====================================================
          LOGOUT POPUP
      ===================================================== */}

      {showLogoutPopup && (

        <div className="popup-overlay">

          <div className="popup-box popup-animate">

            <button
              className="popup-close"
              onClick={closePopup}
            >
              ✕
            </button>

            <h2 className="popup-title popup-title-failure">
              Confirm Logout
            </h2>

            <p className="popup-message">
              Are you sure you want to log out?
            </p>

            <div className="popup-btn-group">

              <button
                className="popup-button popup-basic"
                onClick={closePopup}
              >
                Cancel
              </button>

              <button
                className="popup-button popup-error"
                onClick={confirmLogout}
              >
                Logout
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}

export default Navbar;